import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import { GetProjectEstimatesQuery } from '@/gql/graphql';
import { EmptyState } from '@payaca/ambrosia';
import { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { resolveRecipient, sortContacts, type Contact } from './contacts';
import EstimateItemEditor from './detailPanel/components/EstimateItemEditor';
import EstimateItemPicker from './detailPanel/components/EstimateItemPicker';
import DetailPanel from './detailPanel/DetailPanel';
import EstimateBuilderSidebarHeader from './EstimateBuilderSidebarHeader';
import EstimateFinancials from './EstimateFinancials';
import SendEstimateView from './SendEstimateView';

export type { Contact };

type EstimateBuilderSidebarProps = {
  projectId: string;
  estimateId: string;
};

type SidebarView =
  | { type: 'default' }
  | { type: 'addLineItem'; sectionId: string }
  | { type: 'editLineItem'; lineItemId: string }
  | { type: 'sendEstimate' };

type CustomerContact = NonNullable<
  GetProjectEstimatesQuery['projectById']['estimates'][number]['customerContact']
>;

const buildPinnedRecipient = (
  customerContact: CustomerContact | null | undefined
): Contact | null => {
  if (!customerContact) return null;
  return {
    publicId: customerContact.publicId,
    name: customerContact.name ?? null,
    email: customerContact.email ?? null,
    isPrimary: false,
  };
};

const EstimateBuilderSidebar: FC<EstimateBuilderSidebarProps> = (props) => {
  const { projectId, estimateId } = props;

  const { project, estimate } = useGetProjectEstimate(projectId, estimateId);
  const sections = estimate?.sections ?? [];

  const [view, setView] = useState<SidebarView>({ type: 'default' });
  const [activeSectionId, setActiveSectionId] = useState<string | null>(
    sections[0]?.id ?? null
  );
  const [recipientOverride, setRecipientOverride] = useState<Contact | null>(
    null
  );
  const history = useHistory();

  const goToDefault = () => setView({ type: 'default' });

  const isSent = estimate?.status === 'SENT';
  const isArchived = estimate?.status === 'ARCHIVED';
  // "Resend mode" = the estimate has been sent but isn't archived. In this
  // state the SendEstimateView owns the top bar (reference, status badge,
  // actions menu), so the sidebar header is suppressed to avoid stacking two
  // bars.
  const isResendMode = isSent && !isArchived;
  const contacts = sortContacts(project?.customer?.contacts ?? []);
  // Once SENT, pin to the estimate's stored customerContact so the recipient
  // reflects who actually received it even if the contact has since been edited
  // or removed from the customer.
  const recipient = isSent
    ? buildPinnedRecipient(estimate?.customerContact)
    : resolveRecipient(contacts, recipientOverride);

  if (view.type === 'addLineItem') {
    return (
      <EstimateItemPicker
        projectId={projectId}
        estimateId={estimateId}
        sectionId={view.sectionId}
        onClose={goToDefault}
      />
    );
  }

  if (view.type === 'editLineItem') {
    return (
      <EditLineItemView
        projectId={projectId}
        estimateId={estimateId}
        lineItemId={view.lineItemId}
        onBack={goToDefault}
      />
    );
  }

  // If the estimate becomes ARCHIVED while the user is in send mode (e.g. it
  // was archived from another tab and a cache refetch landed), drop back to
  // the default view so the main return renders the archived placeholder
  // instead of the send UI.
  if (view.type === 'sendEstimate' && !isArchived) {
    return (
      <SendEstimateView
        mode="send"
        projectId={projectId}
        estimateId={estimateId}
        recipient={recipient}
        contacts={contacts}
        onSelectRecipient={setRecipientOverride}
        onBack={goToDefault}
        onSent={() => history.push(`/projects/${projectId}/estimates`)}
      />
    );
  }

  const showSidebarHeader = !isResendMode;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {showSidebarHeader && (
        <EstimateBuilderSidebarHeader
          projectId={projectId}
          estimateId={estimateId}
          estimate={estimate}
          contacts={contacts}
          recipient={recipient}
          onSelectRecipient={setRecipientOverride}
          onEnterSendMode={() => setView({ type: 'sendEstimate' })}
        />
      )}

      <div className="min-h-0 flex-1 overflow-y-auto flex flex-col">
        {!isSent && (
          <EstimateFinancials projectId={projectId} estimateId={estimateId} />
        )}

        {isArchived ? (
          <EmptyState
            className="flex h-full flex-col items-center justify-center text-center"
            iconName="archive"
            text="This estimate is archived and edits aren't allowed. Unarchive to enable editing."
          />
        ) : isSent ? (
          <SendEstimateView
            mode="resend"
            projectId={projectId}
            estimateId={estimateId}
            recipient={recipient}
            onSent={() => history.push(`/projects/${projectId}/estimates`)}
          />
        ) : (
          <DetailPanel
            projectId={projectId}
            estimateId={estimateId}
            activeSectionId={activeSectionId}
            onSetActiveSectionId={setActiveSectionId}
            onAddLineItem={(sectionId) =>
              setView({ type: 'addLineItem', sectionId })
            }
            onEditLineItem={(lineItemId) =>
              setView({ type: 'editLineItem', lineItemId })
            }
          />
        )}
      </div>
    </div>
  );
};

type EditLineItemViewProps = {
  projectId: string;
  estimateId: string;
  lineItemId: string;
  onBack: () => void;
};

const EditLineItemView: FC<EditLineItemViewProps> = (props) => {
  const { projectId, estimateId, lineItemId, onBack } = props;
  const { estimate } = useGetProjectEstimate(projectId, estimateId);
  const lineItem = estimate?.sections
    .flatMap((s) => s.lineItems)
    .find((li) => li.id === lineItemId);

  // If the line item disappears from cache (deleted, or section refetch dropped
  // it), fall back to the default view.
  useEffect(() => {
    if (!estimate) return;
    if (!lineItem) onBack();
  }, [estimate, lineItem, onBack]);

  if (!lineItem) return null;

  return (
    <EstimateItemEditor
      key={lineItem.id}
      projectId={projectId}
      estimateId={estimateId}
      lineItem={lineItem}
      onBack={onBack}
    />
  );
};

export default EstimateBuilderSidebar;
